import React, { useState } from 'react';
import DateAndTime from 'date-and-time';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { SubmitButton } from 'components/Form/Buttons';
import Button from 'components/Buttons/Button';
import {
  currentUserFirstLastNameSelector,
  currentUserLookupIdSelector,
  selectCurrentUserEmail,
} from 'redux/selectors';
import useIsUserAuthenticated from 'utils/hooks/useIsUserAuthenticated';
import * as CheckoutActions from 'components/Checkout/redux/actions';
import { fnAddEventRegistrationCancellationToCart } from 'components/EventRegistration/redux/eventHelpers';
import { fnIsEventRegistrable } from 'components/Events/helpers';
import RegistrationWindowClosed from '../RegistrationWindowClosed';
import CancelEventRegistrationModal from '../CancelEventRegistration';
import { sFormId } from './helpers';

const MultiLevelEventDetailsFooter = ({ oEvent }) => {
  const fnNavigate = useNavigate();
  const fnDispatch = useDispatch();
  const bIsAuthenticated = useIsUserAuthenticated();
  const sCurrentUserLookupId = useSelector(currentUserLookupIdSelector);
  const oCurrentUserName = useSelector(currentUserFirstLastNameSelector);
  const sCurrentUserEmail = useSelector(selectCurrentUserEmail);
  const [
    bShowCancelRegistrationConfirmModal,
    fnSetShowCancelRegistrationConfirmModal,
  ] = useState(false);

  const fnBeginAllRegistrationCancellation = () => {
    // Add the user's information to the cart
    fnDispatch(
      CheckoutActions.update({
        fullName: oCurrentUserName.sName,
        firstName: oCurrentUserName.sPreferredName,
        lastName: oCurrentUserName.sLastName,
        email: sCurrentUserEmail,
        userLookupId: sCurrentUserLookupId,
      })
    );
    // Add all registered sub events to the cart for cancellation (handles 3-level events)
    oEvent.SUB_EVENTS.forEach((oSubEvent) => {
      // Check if this is a grouping level with its own sub events
      if (oSubEvent.SUB_EVENTS) {
        oSubEvent.SUB_EVENTS.forEach((oSubSubEvent) => {
          if (
            oSubSubEvent?.REGISTRATION?.IS_REGISTERED === 1 &&
            !oSubSubEvent.IS_CANCELLED
          ) {
            fnAddEventRegistrationCancellationToCart(oSubSubEvent);
          }
        });
      } else if (
        oSubEvent?.REGISTRATION?.IS_REGISTERED === 1 &&
        !oSubEvent.IS_CANCELLED
      ) {
        fnAddEventRegistrationCancellationToCart(oSubEvent);
      }
    });
    fnSetShowCancelRegistrationConfirmModal(true);
  };

  const bHasCancellableSubEvents = oEvent.SUB_EVENTS.some((oSubEvent) => {
    // Check if this is a grouping level with its own sub events
    if (oSubEvent.SUB_EVENTS) {
      return oSubEvent.SUB_EVENTS.some(
        (oSubSubEvent) =>
          oSubSubEvent.REGISTRATION.IS_REGISTERED === 1 &&
          !oSubSubEvent.IS_CANCELLED &&
          oSubSubEvent.MY_PARTY.HOST.LOOKUPID === sCurrentUserLookupId // Only the host can cancel registrations
      );
    }
    return (
      oSubEvent?.REGISTRATION?.IS_REGISTERED === 1 &&
      !oSubEvent.IS_CANCELLED &&
      oSubEvent.MY_PARTY.HOST.LOOKUPID === sCurrentUserLookupId // Only the host can cancel registrations
    );
  });
  const bIsRegistrationWindowClosed = oEvent?.REGISTRATION?.CLOSE_DATETIME
    ? DateAndTime.parse(
        oEvent.REGISTRATION.CLOSE_DATETIME,
        'YYYY-MM-DDTHH:mm:ss'
      ) < new Date()
    : false;
  const bHasRegistrableSubEvents = oEvent.SUB_EVENTS.some((oSubEvent) => {
    // This sub event could just be a grouping, so check its sub events
    if (oSubEvent.SUB_EVENTS) {
      return oSubEvent.SUB_EVENTS.some((oSubSubEvent) =>
        fnIsEventRegistrable(oSubSubEvent)
      );
    }
    return fnIsEventRegistrable(oSubEvent);
  });

  let continueButtonMarkup = null;
  switch (true) {
    case bIsRegistrationWindowClosed:
      continueButtonMarkup = (
        <RegistrationWindowClosed
          sCloseDateTime={oEvent.REGISTRATION.CLOSE_DATETIME}
          sTimeZone={oEvent.TIME_ZONE}
        />
      );
      break;
    case bHasRegistrableSubEvents:
      continueButtonMarkup = (
        <button
          type='submit'
          form={sFormId}
          className='multiLevelEventDetails__continue'
          data-cy='continue-button'
        >
          Continue
        </button>
      );
      break;
    default:
      break;
  }

  return (
    <>
      {bHasCancellableSubEvents && (
        <div className='multiLevelEventDetails__cancelAllRegistrations t-paragraph--medium'>
          <Button
            sFlavor='underline'
            fnHandleClick={() => fnBeginAllRegistrationCancellation()}
          >
            Cancel All Registered Events
          </Button>
        </div>
      )}

      <div className='multiLevelEventDetails__backContinue'>
        {bIsAuthenticated && (
          <Button sFlavor='secondary' fnHandleClick={() => fnNavigate(-1)}>
            Back
          </Button>
        )}
        <div className='multiLevelEventDetailsFooter__right'>
          {continueButtonMarkup}
        </div>
      </div>
      {bShowCancelRegistrationConfirmModal && (
        <CancelEventRegistrationModal
          bShowCancelRegistrationConfirmModal={
            bShowCancelRegistrationConfirmModal
          }
          fnSetShowCancelRegistrationConfirmModal={
            fnSetShowCancelRegistrationConfirmModal
          }
        />
      )}
    </>
  );
};

MultiLevelEventDetailsFooter.propTypes = {
  oEvent: PropTypes.shape({
    TIME_ZONE: PropTypes.string.isRequired,
    SUB_EVENTS: PropTypes.arrayOf(
      PropTypes.shape({
        ID: PropTypes.string.isRequired,
        TITLE: PropTypes.string.isRequired,
        REGISTRATION: PropTypes.shape({
          IS_REGISTERED: PropTypes.number,
          CLOSE_DATETIME: PropTypes.string,
        }),
        SUB_EVENTS: PropTypes.arrayOf(
          PropTypes.shape({
            ID: PropTypes.string.isRequired,
            TITLE: PropTypes.string.isRequired,
            REGISTRATION: PropTypes.shape({
              IS_REGISTERED: PropTypes.number,
              CLOSE_DATETIME: PropTypes.string.isRequired,
            }).isRequired,
          })
        ),
      })
    ),
  }).isRequired,
};

export default MultiLevelEventDetailsFooter;
